import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Switch, Box,
  MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface AgencyBrief {
  id: number;
  name: string;
  code: string;
}

interface Unit {
  id: number;
  tenant_id: number;
  agency_id: number | null;
  name: string;
  code: string;
  unit_type: string;
  is_active: boolean;
  notes: string | null;
}

const UNIT_TYPES = ['engine', 'ladder', 'rescue', 'ems', 'battalion', 'hazmat', 'brush', 'tanker', 'other'];
const EMPTY_FORM = { name: '', code: '', unit_type: 'engine', agency_id: '' as string, notes: '' };

export default function UnitsPage() {
  const { tenantId } = useAuth();
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [agencies, setAgencies] = useState<AgencyBrief[]>([]);

  const loadAgencies = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/api/tenants/${tenantId}/agencies`);
      setAgencies(res.data);
    } catch { /* ignore */ }
  }, [tenantId]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/units`);
      setUnits(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load units');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); loadAgencies(); }, [load, loadAgencies]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = (u: Unit) => {
    setEditingId(u.id);
    setForm({ name: u.name, code: u.code, unit_type: u.unit_type, agency_id: u.agency_id?.toString() || '', notes: u.notes || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId) return;
    const payload = { name: form.name, code: form.code, unit_type: form.unit_type, agency_id: form.agency_id ? parseInt(form.agency_id) : null, notes: form.notes || null };
    try {
      if (editingId) {
        await api.put(`/api/tenants/${tenantId}/units/${editingId}`, payload);
      } else {
        await api.post(`/api/tenants/${tenantId}/units`, payload);
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this unit?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/units/${id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleActive = async (u: Unit) => {
    if (!tenantId) return;
    try {
      await api.put(`/api/tenants/${tenantId}/units/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Update failed');
    }
  };

  const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Units</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Unit</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Agency</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center">Loading...</TableCell></TableRow>
            ) : units.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">No units configured</TableCell></TableRow>
            ) : units.map(u => (
              <TableRow key={u.id}>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.code}</TableCell>
                <TableCell>{agencies.find(a => a.id === u.agency_id)?.name || '—'}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{u.unit_type}</TableCell>
                <TableCell><Switch checked={u.is_active} onChange={() => handleToggleActive(u)} size="small" /></TableCell>
                <TableCell>{u.notes}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(u)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(u.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Unit' : 'Add Unit'}</DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth margin="dense" value={form.name} onChange={updateField('name')} required
            helperText="Display name (e.g., Engine 1)" />
          <TextField label="Code" fullWidth margin="dense" value={form.code} onChange={updateField('code')} required
            helperText="Unit identifier used in CAD dispatch (e.g., E1)" />
          <TextField label="Agency" fullWidth margin="dense" select
            value={form.agency_id} onChange={updateField('agency_id')}>
            <MenuItem value="">— No Agency —</MenuItem>
            {agencies.map(a => <MenuItem key={a.id} value={a.id.toString()}>{a.name}</MenuItem>)}
          </TextField>
          <FormControl fullWidth margin="dense">
            <InputLabel>Unit Type</InputLabel>
            <Select value={form.unit_type} label="Unit Type"
              onChange={(e) => setForm(prev => ({ ...prev, unit_type: e.target.value }))}>
              {UNIT_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Notes" fullWidth margin="dense" value={form.notes} onChange={updateField('notes')} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || !form.code}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
